import type { ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface SocialPlatform {
	id: string;
	label: string;
	// Official brand colors — the one place raw hex is allowed.
	gradient: string;
	icon: ReactNode;
}

function BrandIcon({ path }: { path: string }) {
	return (
		<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
			<path d={path} />
		</svg>
	);
}

const SOCIAL_PLATFORMS: SocialPlatform[] = [
	{
		id: "instagram",
		label: "Instagram",
		gradient: "from-[#f9ce34] via-[#ee2a7b] to-[#6228d7]",
		icon: (
			<BrandIcon path="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
		),
	},
	{
		id: "telegram",
		label: "Telegram",
		gradient: "from-[#2AABEE] to-[#229ED9]",
		icon: (
			<BrandIcon path="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
		),
	},
	{
		id: "linkedin",
		label: "LinkedIn",
		gradient: "from-[#0077B5] to-[#00A0DC]",
		icon: (
			<BrandIcon path="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
		),
	},
	{
		id: "facebook",
		label: "Facebook",
		gradient: "from-[#1877F2] to-[#42A5F5]",
		icon: (
			<BrandIcon path="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
		),
	},
	{
		id: "youtube",
		label: "YouTube",
		gradient: "from-[#FF0000] to-[#FF4444]",
		icon: (
			<BrandIcon path="M23.495 6.205a3.007 3.007 0 0 0-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 0 0 .527 6.205a31.247 31.247 0 0 0-.522 5.805 31.247 31.247 0 0 0 .522 5.783 3.007 3.007 0 0 0 2.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 0 0 2.088-2.088 31.247 31.247 0 0 0 .5-5.783 31.247 31.247 0 0 0-.5-5.805zM9.609 15.601V8.408l6.264 3.602z" />
		),
	},
	{
		id: "tiktok",
		label: "TikTok",
		gradient: "from-[#010101] to-[#333]",
		icon: (
			<BrandIcon path="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
		),
	},
];

interface SocialPlatformModalProps {
	open: boolean;
	onClose: () => void;
	selected: string | null;
	onSelect: (platformId: string | null) => void;
	onContinue: () => void;
	continueDisabled?: boolean;
	subtitle?: string;
}

// Single-platform picker for the Content Plan tool.
export function SocialPlatformModal({
	open,
	onClose,
	selected,
	onSelect,
	onContinue,
	continueDisabled = false,
	subtitle = "Select social media platforms",
}: SocialPlatformModalProps) {
	return (
		<AnimatePresence>
			{open && (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					className="fixed inset-0 z-[100] flex items-center justify-center bg-scrim/30 backdrop-blur-sm"
					onClick={onClose}
				>
					<motion.div
						initial={{ opacity: 0, scale: 0.94, y: 12 }}
						animate={{ opacity: 1, scale: 1, y: 0 }}
						exit={{ opacity: 0, scale: 0.94, y: 12 }}
						transition={{ duration: 0.2, ease: "easeOut" }}
						onClick={(e) => e.stopPropagation()}
						className="relative w-full max-w-md mx-4 rounded-2xl border border-border-subtle bg-bg-surface shadow-popover overflow-hidden"
					>
						<div className="flex items-center justify-between px-6 py-4">
							<div>
								<h2 className="text-base font-semibold text-text-primary">
									Content Plan
								</h2>
								<p className="text-xs text-text-tertiary mt-0.5">{subtitle}</p>
							</div>
							<Button variant="ghost" size="icon-sm" onClick={onClose}>
								<X size={16} />
							</Button>
						</div>

						<div className="px-6 py-5">
							<div className="grid grid-cols-3 gap-3">
								{SOCIAL_PLATFORMS.map((platform) => {
									const isSelected = selected === platform.id;
									return (
										<button
											key={platform.id}
											type="button"
											onClick={() => onSelect(isSelected ? null : platform.id)}
											className={cn(
												"relative flex flex-col items-center gap-2 px-3 py-4 rounded-xl border-2 transition-all",
												isSelected
													? "border-brand-700 bg-brand-50"
													: "border-border-subtle bg-bg-canvas hover:border-border-control hover:bg-bg-sunken",
											)}
										>
											<div
												className={cn(
													"w-10 h-10 rounded-lg bg-gradient-to-br flex items-center justify-center text-white shadow-lg",
													platform.gradient,
												)}
											>
												{platform.icon}
											</div>
											<span className="text-xs font-medium text-text-secondary">
												{platform.label}
											</span>
											{isSelected && (
												<div className="absolute top-2 right-2 w-4 h-4 rounded-full bg-brand-400 text-text-on-brand flex items-center justify-center">
													<svg width="9" height="9" viewBox="0 0 12 12" fill="none">
														<path
															d="M2 6l3 3 5-5"
															stroke="currentColor"
															strokeWidth="2"
															strokeLinecap="round"
															strokeLinejoin="round"
														/>
													</svg>
												</div>
											)}
										</button>
									);
								})}
							</div>
						</div>

						<div className="flex items-center justify-between px-6 py-4">
							<span className="text-xs text-text-tertiary">
								{selected ? "1 selected" : "Select a platform"}
							</span>
							<div className="flex items-center gap-2">
								<Button variant="secondary" size="sm" onClick={onClose}>
									Cancel
								</Button>
								<Button
									size="sm"
									disabled={!selected || continueDisabled}
									onClick={onContinue}
								>
									Continue
								</Button>
							</div>
						</div>
					</motion.div>
				</motion.div>
			)}
		</AnimatePresence>
	);
}
